import { assign, fromPromise, setup } from 'xstate';

import { logger } from '../../../../../helpers/logging';
import { sendAuthScreenViewedEvent, sendOAuthButtonClickedEvent } from '../../../helpers/events';
import { sendAuthButtonClickedEvent } from '../../../helpers/events/sendAuthButtonClickedEvent';

import { context as initialContext } from './data/context';
import { handleOAuth, handleOAuthError } from './helpers/oauth';
import { handleStandardAuth, handleStandardAuthError } from './helpers/standardAuth';
import type { Context, Events } from './types';

export const machine = setup({
  types: {} as { context: Context; events: Events; input: Partial<Context> },
  actions: {
    clearStandardAuthErrors: assign(({ context: { standardAuth } }) => ({
      standardAuth: { ...standardAuth, errors: [] },
    })),
    clearOAuthErrors: assign(({ context: { OAuth } }) => ({ OAuth: { ...OAuth, errors: [] } })),
    sendAuthLoginClickedEvent: ({ context }) => {
      sendAuthButtonClickedEvent('email', 'login')(context);
    },
    sendAuthScreenViewedEvent: ({ context }) => {
      sendAuthScreenViewedEvent('login')(context);
    },
    sendOAuthButtonClickedEvent: ({ context }) => {
      sendOAuthButtonClickedEvent(context);
    },
    sendSwitchToSignUpEvent: ({ context }) => {
      sendAuthButtonClickedEvent('signup', 'login')(context);
    },
    setAccount: assign(({ context: { standardAuth }, event }) => ({
      ...(event.type === 'LOGIN_VIA_CREDENTIALS' && {
        standardAuth: { ...standardAuth, account: event.data.account },
      }),
    })),
    setInvalidLoginAttempts: assign(({ context: { standardAuth } }) => ({
      standardAuth: {
        ...standardAuth,
        invalidLoginAttempts: standardAuth.invalidLoginAttempts + 1,
      },
    })),
    setStandardAuthErrors: assign(({ context: { standardAuth }, event }) => {
      logger.debug('setStandardAuthErrors', standardAuth.errors);
      return {
        ...(event.type === 'xstate.error.actor.handleStandardAuth' && {
          standardAuth: {
            ...standardAuth,
            errors: [
              {
                errorMessage: handleStandardAuthError(
                  event.error,
                  standardAuth.invalidLoginAttempts,
                ),
              },
            ],
          },
        }),
      };
    }),
    setIsLoading: assign({ isLoading: (_, params: { isLoading: boolean }) => params.isLoading }),
    setOAuth: assign(({ event }) => ({
      ...(event.type === 'LOGIN_VIA_OAUTH' && {
        OAuth: { ...event.data.OAuth, errors: [], source: 'login' as const },
      }),
    })),
    setOAuthErrors: assign(({ context: { OAuth }, event }) => ({
      ...(event.type === 'xstate.error.actor.handleOAuth' && {
        OAuth: { ...OAuth, errors: [{ errorMessage: handleOAuthError(event.error) }] },
      }),
    })),
    setReCaptchaRef: assign(({ context: { standardAuth }, event }) => ({
      ...(event.type === 'LOGIN_VIA_CREDENTIALS' && {
        standardAuth: { ...standardAuth, reCaptcha: { ref: event.data.reCaptcha.ref } },
      }),
    })),
    setUserIsLoggedInTrue: assign({ user: { isLoggedIn: true } }),
    resetInvalidLoginAttempts: assign(({ context: { standardAuth } }) => ({
      standardAuth: {
        ...standardAuth,
        invalidLoginAttempts: 0,
      },
    })),
  },
  actors: {
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    handleStandardAuth: fromPromise<void, Context>(async ({ input }) => handleStandardAuth(input)),
    handleOAuth: fromPromise<boolean, Context>(async ({ input }) => handleOAuth(input)),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    resetReCaptcha: fromPromise<void, Context>(async ({ input: { standardAuth } }) => {
      const { ref } = standardAuth.reCaptcha;
      if (ref?.current) {
        await ref.current.reset();
      }
    }),
  },
  guards: { isUserLoggedIn: ({ context: { user } }) => user.isLoggedIn },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QBsD2UCWA7AsgQwGMALbMAOgCcw8IBPAYgG0AGAXUVAAdVYMAXDKiwcQAD0QBaAKxkATADYp8gIwBmWQE4AHMoAsUrcw0AaELUSaA7GUUaNUjassbZWywF93ptJlyESWORUNAyMyuxIINy8AkIi4ghqqmRasrrMzMrymrKqyrJSpuYIqqlkzFpSynYVFVLM8p7e6Nj4xKRkxKg8YDhgfESoEPQAMgDyAOIAkgByAPoAalMAgnMAwgBKAKIAIlszACorIwDKLBFcPPyCwpEJyszJ8hq6ylqKubqqUpayRYi6SzMcq6bSWSyg5iA1SqJogHytfwdLo9PoDIajSazRYrOZjZYAVQOAAlziJotc4ndEKkNGRwZopEyHKDQf8ELpFGRAVJdLJwQ5LN9YV54S0-O1Ap1Bqj+oNhicAOpTA5rYlzA5jOYnKYTGYEgAKZMiFNit1ACV0Wi0NnkWkBz1KmWYhTMFiUZGUTNkPqZzB9yg8ooREoC5FgAFcAEYAW34AiwUDWVAgYCwAjwyFg9AgQnI2AAbqgANbkIh4LAQZBgE58CsQPAUCDLCMDY2XGI3eLu5TczLKL0u+Tyb5Q9laRzlX5Myz5RxuEXNXxtMNkSOx+PYJMptMZrP0MAUCioChkTjIPB8ABmJ5jZHLlertfrjebraI7aiVzN3ZKGl7EJSKouhWnoqh2CYboIFU8gpICPoaEYaRQkGS6IpK4Z1hQCZQPi745nmZCFiWZb1tWeFtmw5Lfl21IILOvayEkGjPI4FQQuOiGek4fKPMBqjyLocIhiuHSwFhOEUUQB5HieZ4Xtet73mRYBSZ+pq0RaiDyC6ci-PydqpAJ-LsjB9L6PazjZDoDieKKWBDHAIgiUigTUZ2VJaQgEgqHIfKBg8XyvAhpmyGQTJ2A8micjkliNMG4qiVKwR0O5lLmmIAJ-FBbw2qoRgTq42TehoqFisurnkCisC9HKQxpT+dFWjaliVPIQoQkh+iusU3ywTCVT2PkuRxV6wmJZVa7RnGfA4cmkC7hgmbwCaNGeZliR2ik2T+pYQGGHkrXsqx9J5PU+gPEoPrjRVGFrhJW5SQ1mkbe1YVKKCvKlVa8jskCYVqPYFQDl6Ak3ehq68FAWAEpwJxgNWBB8JAz3rQk9S9ud4HPF6Lz+uybzWC6dhpFowFZP6Wjg6GHQhicEYEAQcCwFeEbIKjGUJBIah+Uxs5QuTCHKOy2gyN8ov2ioQrqHZ7hAA */
  id: 'loginMachine',
  context: ({ input }) => ({ ...initialContext, ...input }),
  initial: 'ready',
  entry: ['sendAuthScreenViewedEvent'],
  states: {
    ready: {
      always: [{ guard: 'isUserLoggedIn', target: 'loginSuccessful' }, { target: 'chooseMethod' }],
    },
    chooseMethod: {
      invoke: {
        input: ({ context }) => context,
        src: 'resetReCaptcha',
      },
      on: {
        LOGIN_VIA_CREDENTIALS: {
          actions: [
            'clearStandardAuthErrors',
            'clearOAuthErrors',
            'setAccount',
            'setReCaptchaRef',
            'sendAuthLoginClickedEvent',
          ],
          target: 'submittingCredentials',
        },
        LOGIN_VIA_OAUTH: {
          actions: [
            'clearStandardAuthErrors',
            'clearOAuthErrors',
            'setOAuth',
            'sendOAuthButtonClickedEvent',
          ],
          target: 'startingOAuth',
        },
        SWITCH_TO_SIGNUP: { actions: ['sendSwitchToSignUpEvent'], target: 'signUpSelected' },
      },
    },
    submittingCredentials: {
      entry: [{ type: 'setIsLoading', params: { isLoading: true } }],
      invoke: {
        id: 'handleStandardAuth',
        input: ({ context }) => context,
        src: 'handleStandardAuth',
        onDone: { actions: ['setUserIsLoggedInTrue'], target: 'loginSuccessful' },
        onError: {
          actions: ['setInvalidLoginAttempts', 'setStandardAuthErrors'],
          target: 'chooseMethod',
        },
      },
      exit: [{ type: 'setIsLoading', params: { isLoading: false } }],
    },
    startingOAuth: {
      entry: [{ type: 'setIsLoading', params: { isLoading: true } }],
      invoke: {
        id: 'handleOAuth',
        input: ({ context }) => context,
        src: 'handleOAuth',
        onDone: {
          actions: ['resetInvalidLoginAttempts', 'setUserIsLoggedInTrue'],
          target: 'loginSuccessful',
        },
        onError: { actions: ['setOAuthErrors'], target: 'chooseMethod' },
      },
      exit: [{ type: 'setIsLoading', params: { isLoading: false } }],
    },
    signUpSelected: { type: 'final', output: { isLoginFinished: false } },
    loginSuccessful: { type: 'final', output: { isLoginFinished: true } },
  },
  output: ({ event }) => event.output,
});
